import { HtmlParser } from '@calendly/html-parser'
import { I18n } from '@calendly/i18n'
import { Badge } from '@calendly/ui/components/badge'
import { Button } from '@calendly/ui/components/button'
import { AiSparkleIcon, ArrowChevronRightMiniIcon, QuestionCircleIcon } from '@calendly/ui/components/icons'
import { PageHeader } from '@calendly/ui/components/page-header'
import { BodyText, TitleText } from '@calendly/ui/components/typography'
import { useSelector } from 'react-redux'

import { isAvailableFeatureSelector } from 'features/billing'
import { AdminDisabledNotetakerBanner } from 'features/meeting_intelligence/components'
import { SampleRecapButton } from 'features/meeting_intelligence/components/sample_recap_button'
import { useNotetakerFreeMeetingsQuota } from 'features/meeting_intelligence/hooks'
import { LearnMoreLinkMeetingRecaps } from 'features/meeting_intelligence/utils'
import { useIsSettingsModalLayout } from 'features/settings_modal/layout_context'
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events/constants'
import { useTrackOnce } from 'hooks/analytics_events/useTrack'
import { useNotetakerUpsellUIVisible } from 'hooks/notetaker_visibility'
import { useNotetakerFiveFreeRecapsExperiment } from 'hooks/useNotetakerFiveFreeRecapsExperiment'
import { useNotetakerFreeExperiment } from 'hooks/useNotetakerFreeExperiment'

import { ReengagementIllustration } from './reengagement_illustration'
import {
  ButtonContainer,
  CopyWrapper,
  LandingImage,
  LandingPageContainer,
  ReengagementCopyWrapper,
  ReengagementDeleteFooter,
  ReengagementIllustrationColumn,
  ReengagementLandingPageContainer,
  reengagementModalPaddingCss,
  reengagementStandalonePaddingCss,
} from './styles'
import LandingPageImage from '../../assets/user-MI-illustration.png'
import { ModifiedPageContainer } from '../../user/styles'
import { DeleteFooter } from '../delete_footer'
import { NotetakerDisabledEnablementSection } from '../notetaker_enablement_section/notetaker_enablement_section'

const NOTETAKER_LEARN_MORE_URL = 'https://calendly.com/help/notetaker-overview'

export const EmptyState = ({
  setShowDeleteModal,
  handleTrackModal,
  wasPreviouslyEnabled,
  handleEmptyStateClick,
  handleEnableToggle,
  adminDisabledNotetaker
}) => {
  const isMeetingIntelligenceAvailable = useSelector((state) => isAvailableFeatureSelector(state, { featureKey: 'meeting_intelligence' }))
  const { isInExperiment: isInNotetakerFreeExperiment } = useNotetakerFreeExperiment()
  const { isInExperiment: isInNotetakerFiveFreeRecapsExperiment } = useNotetakerFiveFreeRecapsExperiment()
  const {
    isVisible: upsellUiVisible,
    isLoading: isUpsellUiVisibilityLoading
  } = useNotetakerUpsellUIVisible()
  const isInMeteredNotetakerExperiment = isInNotetakerFreeExperiment || isInNotetakerFiveFreeRecapsExperiment
  const { hasRemainingFreeMeetings } = useNotetakerFreeMeetingsQuota({
    skip: !isInMeteredNotetakerExperiment
  })
  const inSettingsModal = useIsSettingsModalLayout()
  const shouldShowPrimaryAction = hasRemainingFreeMeetings
    || isMeetingIntelligenceAvailable
    || (upsellUiVisible && !isUpsellUiVisibilityLoading)

  useTrackOnce(!wasPreviouslyEnabled && shouldShowPrimaryAction, {
    name: TRACKING_EVENT_NAMES.CTA_SHOWN,
    entities: ['attribution', 'organization', 'user'],
    properties: {
      cta_key: 'ai_notetaker_settings',
      cta_text: 'get_started_ai_notetaker',
      cta_destination: 'ai_notetaker_setup_flow',
    }
  })

  const getButtonText = () => {
    if (hasRemainingFreeMeetings) return I18n.t('publisher.billing.meeting_intelligence.get_notetaker')

    if (isMeetingIntelligenceAvailable) return I18n.t('publisher.billing.meeting_intelligence.try_notetaker')

    return I18n.t('publisher.billing.meeting_intelligence.upgrade_plan')
  }

  const handleLearnMoreClick = () => {
    window.open(NOTETAKER_LEARN_MORE_URL, '_blank', 'noopener,noreferrer')
  }

  const renderReengagementActions = () => (
    <ButtonContainer>
      <SampleRecapButton ctaKey="ai_notetaker_settings" />
      <Button decoration="secondary-outline" onClick={handleLearnMoreClick}>
        <QuestionCircleIcon />
        {I18n.t('publisher.personal_settings.meeting_recaps.enablement.learn_more_button')}
        <ArrowChevronRightMiniIcon />
      </Button>
    </ButtonContainer>
  )

  const renderFirstTimeActions = () => (
    <ButtonContainer>
      {shouldShowPrimaryAction && (
        <Button decoration="meeting-intelligence" onClick={handleEmptyStateClick} disabled={adminDisabledNotetaker}>
          <AiSparkleIcon />
          {getButtonText()}
        </Button>
      )}
      <SampleRecapButton ctaKey="ai_notetaker_settings" />
    </ButtonContainer>
  )

  return (
    <ModifiedPageContainer>
      {!inSettingsModal && (
        <PageHeader
          badge={(
            adminDisabledNotetaker ? <Badge type="error">{I18n.t('publisher.personal_settings.meeting_recaps.disabled')}</Badge> : null
          )}
          eyebrow={[
            <BodyText weight="bold" color="muted">
              {I18n.t('publisher.personal_settings.navigation.account_details')}
            </BodyText>
          ]}
          title={I18n.t('publisher.personal_settings.meeting_recaps.meeting_recaps')}
        />
      )}
      <AdminDisabledNotetakerBanner adminDisabledNotetaker={adminDisabledNotetaker} />
      {wasPreviouslyEnabled ? (
        <ReengagementLandingPageContainer
          className={inSettingsModal ? reengagementModalPaddingCss : reengagementStandalonePaddingCss}
        >
          <ReengagementCopyWrapper>
            <NotetakerDisabledEnablementSection
              wasPreviouslyEnabled
              embedded
              disabled={adminDisabledNotetaker}
              onToggle={handleEnableToggle}
              inSettingsModal={inSettingsModal}
              data-testid="notetaker-reengagement-section"
            />
            {renderReengagementActions()}
            <ReengagementDeleteFooter>
              <DeleteFooter handleTrackModal={handleTrackModal} setShowDeleteModal={setShowDeleteModal} />
            </ReengagementDeleteFooter>
          </ReengagementCopyWrapper>
          <ReengagementIllustrationColumn>
            <ReengagementIllustration />
          </ReengagementIllustrationColumn>
        </ReengagementLandingPageContainer>
      ) : (
        <LandingPageContainer>
          <CopyWrapper>
            <TitleText size="medium">{I18n.t('publisher.personal_settings.meeting_recaps.landing.title')}</TitleText>
            <BodyText>
              {HtmlParser(I18n.t('publisher.personal_settings.meeting_recaps.landing.description'))}
            </BodyText>
            <div>
              <BodyText>
                {HtmlParser(
                  I18n.t('publisher.personal_settings.meeting_recaps.landing.learn_more'),
                  { LearnMoreLinkMeetingRecaps }
                )}
              </BodyText>
            </div>
            {renderFirstTimeActions()}
          </CopyWrapper>
          <LandingImage src={LandingPageImage} alt="" />
        </LandingPageContainer>
      )}
    </ModifiedPageContainer>
  )
}
